import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Card,
  Button,
  Input,
  message,
  Form,
  Modal,
  Space,
  Spin,
} from "antd";
import { EditOutlined, SaveOutlined, CloseOutlined, FileTextOutlined } from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { Title } = Typography;
const { TextArea } = Input;

const News = () => {
  // Default fallback data (includes both title and content)
  const defaultData = {
    title: "Daily Highlights",
    content: `<h4>Breaking News: New Frontend Feature Released</h4>
<p>We are excited to announce our new admin panel feature that now includes a dedicated section for news and updates. You can write plain text or include HTML code to further customize the layout.</p>
<!-- Example video element -->
<video controls width="600">
  <source src="https://www.w3schools.com/html/mov_bbb.mp4" type="video/mp4">
  Your browser does not support the video tag.
</video>
<p>Stay tuned for more updates.</p>`,
  };

  const [newsData, setNewsData] = useState(defaultData);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [form] = Form.useForm();
  const [isDirty, setIsDirty] = useState(false);
  // Keep reference of the last saved news data to allow cancellation of unsaved changes.
  const currentDataRef = useRef(newsData);
  // Ref for the content container in view mode.
  const contentRef = useRef(null);

  // Fetch news data from backend API
  const fetchNews = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/admin/news");
      const result = response.data;
      if (result.success) {
        setNewsData(result.news);
        currentDataRef.current = result.news;
      } else {
        message.error("Failed to load news data");
      }
    } catch (error) {
      message.error(
        error.response?.data?.message || error.message || "Error fetching news."
      );
      console.error("Error fetching news data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // When newsData updates in view mode, adjust any video or iframe elements to be responsive.
  useEffect(() => {
    if (!isEditing && contentRef.current) {
      const videos = contentRef.current.getElementsByTagName("video");
      const iframes = contentRef.current.getElementsByTagName("iframe");
      [...videos, ...iframes].forEach((media) => {
        media.style.maxWidth = "100%";
        media.style.height = "auto";
        // Optional: center the video if desired
        media.style.display = "block";
        media.style.margin = "0 auto";
      });
    }
  }, [newsData, isEditing]);

  // Fetch data on component mount.
  useEffect(() => {
    fetchNews();
  }, []);

  // Save changes to the news record via the PUT API.
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const updatedData = {
        title: values.title,
        content: values.content,
      };
      const response = await axiosInstance.put(
        "/admin/news",
        updatedData,
        { headers: { "Content-Type": "application/json" } }
      );
      const result = response.data;
      if (response.status === 200 && result.success) {
        setNewsData(result.news);
        currentDataRef.current = result.news;
        message.success("News updated successfully.");
        setIsEditing(false);
        setIsDirty(false);
      } else {
        message.error(result.message || "Failed to update news.");
      }
    } catch (error) {
      message.error(
        error.response?.data?.message ||
          error.message ||
          "Error saving news."
      );
      console.error("Error saving news:", error);
    }
  };

  // Cancel editing, prompting the user if there are unsaved changes.
  const handleCancel = () => {
    if (isDirty) {
      Modal.confirm({
        title: "Discard Changes?",
        content: "You have unsaved changes. Are you sure you want to discard them?",
        okText: "Yes",
        cancelText: "No",
        onOk: () => {
          form.resetFields();
          setNewsData(currentDataRef.current);
          setIsEditing(false);
          setIsDirty(false);
        },
      });
    } else {
      setIsEditing(false);
    }
  };

  // Enter edit mode and prepopulate the form with current news data.
  const handleEdit = () => {
    form.setFieldsValue({
      title: newsData.title,
      content: newsData.content,
    });
    setIsEditing(true);
  };

  // While fetching data, render a centered loader.
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-gray-600">Loading news content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                <FileTextOutlined className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  News & Announcements
                </h1>
                <p className="text-gray-600">
                  Manage news content and announcements for the platform
                </p>
              </div>
            </div>

            {!isEditing && (
              <Button 
                type="primary" 
                icon={<EditOutlined />} 
                onClick={handleEdit}
                className="bg-blue-500 hover:bg-blue-600 border-blue-500 h-12 px-6 rounded-xl font-medium shadow-sm hover:shadow-md transition-all duration-200"
                size="large"
              >
                Edit Content
              </Button>
            )}
          </div>
        </div>

        {/* News Content Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {isEditing ? (
            // --- EDIT MODE ---
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <EditOutlined className="text-green-600 text-lg" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Edit News Content
                  </h3>
                  <p className="text-sm text-gray-500">
                    Update the news title and content below
                  </p>
                </div>
              </div>

              <Form
                layout="vertical"
                form={form}
                initialValues={{
                  title: newsData.title,
                  content: newsData.content,
                }}
                onValuesChange={() => setIsDirty(true)}
                className="space-y-6"
              >
                <Form.Item
                  label={<span className="font-medium text-gray-700 text-lg">News Title</span>}
                  name="title"
                  rules={[{ required: true, message: "Please enter the news title" }]}
                >
                  <Input 
                    placeholder="Enter news title..." 
                    className="rounded-xl h-12 text-lg border-2 border-gray-200 hover:border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                    size="large"
                  />
                </Form.Item>
                
                <Form.Item
                  label={
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 text-lg">News Content</span>
                      <span className="text-sm text-gray-500 font-normal">
                        Supports plain text or HTML
                      </span>
                    </div>
                  }
                  name="content"
                  rules={[{ required: true, message: "Please enter the news content" }]}
                >
                  <TextArea
                    placeholder="Enter content here. You may use HTML code to customize the layout..."
                    autoSize={{ minRows: 8, maxRows: 16 }}
                    className="rounded-xl border-2 border-gray-200 hover:border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 font-mono text-sm"
                  />
                </Form.Item>

                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <div className="text-sm text-blue-800">
                    <div className="font-semibold mb-2">HTML Support:</div>
                    <div className="text-xs text-blue-700">
                      You can use HTML tags to format your content. Common tags include:<br />
                      • &lt;h1&gt; to &lt;h6&gt; for headings<br />
                      • &lt;p&gt; for paragraphs<br />
                      • &lt;video&gt; for embedded videos<br />
                      • &lt;img&gt; for images<br />
                      • &lt;a&gt; for links
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                  <Button 
                    onClick={handleCancel}
                    icon={<CloseOutlined />}
                    className="h-12 px-6 rounded-xl border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700 font-medium transition-all duration-200"
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    onClick={handleSave}
                    icon={<SaveOutlined />}
                    className="bg-green-500 hover:bg-green-600 border-green-500 h-12 px-6 rounded-xl font-medium shadow-sm hover:shadow-md transition-all duration-200"
                    size="large"
                  >
                    Save Changes
                  </Button>
                </div>
              </Form>
            </div>
          ) : (
            // --- VIEW MODE ---
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileTextOutlined className="text-blue-600 text-lg" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Current News Content
                  </h3>
                  <p className="text-sm text-gray-500">
                    This content is visible to users
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                {/* News Title */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                  <h2 className="text-2xl font-bold text-gray-900 text-center">
                    {newsData.title}
                  </h2>
                </div>

                {/* News Content */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div
                    ref={contentRef}
                    dangerouslySetInnerHTML={{ __html: newsData.content }}
                    className="news-content prose prose-lg max-w-none"
                  />
                </div>

                {/* Last Updated Info */}
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                  <div className="text-sm text-yellow-800 text-center">
                    <strong>Note:</strong> Click the "Edit Content" button above to modify this news content.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .news-content {
          line-height: 1.6;
        }
        .news-content h1, .news-content h2, .news-content h3, .news-content h4 {
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          font-weight: 600;
          color: #1f2937;
        }
        .news-content h1 {
          font-size: 2em;
          border-bottom: 2px solid #e5e7eb;
          padding-bottom: 0.3em;
        }
        .news-content h2 {
          font-size: 1.5em;
        }
        .news-content h3 {
          font-size: 1.25em;
        }
        .news-content h4 {
          font-size: 1.1em;
        }
        .news-content p {
          margin-bottom: 1em;
          color: #4b5563;
        }
        .news-content video, .news-content iframe {
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          margin: 1.5em 0;
        }
        .news-content img {
          border-radius: 8px;
          max-width: 100%;
          height: auto;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .news-content a {
          color: #2563eb;
          text-decoration: underline;
        }
        .news-content a:hover {
          color: #1d4ed8;
        }
        .news-content ul, .news-content ol {
          margin: 1em 0;
          padding-left: 1.5em;
        }
        .news-content li {
          margin-bottom: 0.5em;
        }
        .news-content blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1em;
          margin: 1.5em 0;
          font-style: italic;
          color: #6b7280;
        }
      `}</style>
    </div>
  );
};

export default News;